//----------------------------------------------------------------------------------------------------------------------
// Mouse Value Reader
//----------------------------------------------------------------------------------------------------------------------

import type { DeviceValueReader, InputState, MouseValueReaderDefinition } from '../../../interfaces/input.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Types of mouse input sources
 */
export type MouseSourceType = 'button' | 'position' | 'wheel';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Reads values from mouse input states
 */
export class MouseValueReader implements DeviceValueReader
{
    /**
     * Which category of mouse input: button press, cursor position, or scroll wheel.
     */
    readonly sourceType : MouseSourceType;

    /**
     * Identifies the input within the source type (e.g. "button-0", "absolute:x", "deltaY")
     */
    readonly sourceKey : string;

    /**
     * Creates a new MouseValueReader
     *
     * @param sourceType
     * @param sourceKey - e.g. "button-0", "absolute:x", "deltaY"
     */
    constructor(sourceType : MouseSourceType, sourceKey : string)
    {
        this.sourceType = sourceType;
        this.sourceKey = sourceKey;
    }

    /**
     * Gets the value of the mouse input source. Returns undefined for non-mouse input states.
     *
     * @param state
     */
    public getValue(state : InputState) : boolean | number | undefined
    {
        // Check if the state is from the correct device type
        if(state.type !== 'mouse')
        {
            return undefined;
        }

        switch (this.sourceType)
        {
            case 'button':
            {
                const buttonState = state.buttons[this.sourceKey];
                return buttonState ? buttonState.pressed : undefined;
            }

            case 'position':
            {
                // Position keys are in format "positionType:axis" (e.g., "absolute:x")
                const [ posType, axis ] = this.sourceKey.split(':');

                if(!posType || !axis || (posType !== 'absolute' && posType !== 'relative')
                    || (axis !== 'x' && axis !== 'y'))
                {
                    return undefined;
                }

                return state.position[posType][axis];
            }

            case 'wheel':
            {
                const isValidWheelKey = this.sourceKey === 'deltaX' || this.sourceKey === 'deltaY'
                    || this.sourceKey === 'deltaZ';
                if(state.wheel && isValidWheelKey)
                {
                    return state.wheel[this.sourceKey];
                }
                return undefined;
            }

            default:
                return undefined;
        }
    }

    /**
     * Creates a MouseValueReader from a colon-delimited string (e.g. "button:0", "position:absolute:x")
     *
     * @param sourceTypeString
     */
    public static fromString(sourceTypeString : string) : MouseValueReader
    {
        const [ sourceType, ...keyParts ] = sourceTypeString.split(':');
        const sourceKey = keyParts.join(':');

        if(!sourceType || !sourceKey)
        {
            throw new Error(`Invalid mouse source format: ${ sourceTypeString }`);
        }

        return new MouseValueReader(
            sourceType as MouseSourceType,
            sourceKey
        );
    }

    /**
     * Returns a JSON-serializable representation of this mouse value reader.
     */
    public toJSON() : MouseValueReaderDefinition
    {
        // Always return consistent 'mouse' type with sourceType and sourceKey
        return {
            type: 'mouse',
            sourceType: this.sourceType,
            sourceKey: this.sourceKey,
        };
    }
}

//----------------------------------------------------------------------------------------------------------------------
